<template>
    <div>
        <v-row>
            <v-col>
                <h4 class="text-h4">Exchange Rate Difference</h4>
                <p class="text-subtitle-1">Exchange Rate Difference Calculation and Adjustment</p>
                <v-divider></v-divider>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="3">
                <v-row>
                    <v-col>
                        <v-autocomplete
                            label="Year"
                            v-model="year"
                            outlined
                            dense
                            hide-details
                            :items="years"
                        ></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-btn color="primary" block @click="fetch">
                            <v-icon left>mdi-calculator</v-icon>
                            calculate gain/loss
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row v-if="data.length > 0 && !currenciesLoading">
                    <v-col>
                        <v-card>
                            <v-card-title>Exchange Rate Adjustment</v-card-title>
                            <v-card-subtitle>Change End of Year <span class="font-weight-bold">{{ year }}</span> exchange rate</v-card-subtitle>
                            <v-card-text>
                                <v-text-field
                                    v-for="(d, i) in currencies"
                                    :key="i"
                                    :label="d.abbreviation"
                                    v-model="currencies[i].rate"
                                    :rules="[isRequired]"
                                    dense
                                ></v-text-field>
                            </v-card-text>
                            <v-card-actions>
                                <v-btn depressed block @click="adjustRate" :loading="adjustRateLoading" v-if="hasPermission('create:exchangeRate')">
                                    <v-icon left>mdi-reload</v-icon>
                                    change exchange rate
                                </v-btn>                               
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-card v-if="netGainLoss != 0">
                            <v-card-title>Generate Journal Voucher</v-card-title>
                            <v-card-text>

                                <v-autocomplete
                                label="Gain/Loss Account"
                                        v-model="account_id"
                                        :rules="[isRequired]"
                                        :search-input.sync="accountQuery"
                                        @update:search-input="searchAccount"
                                        :items="accountList"
                                        item-value="id"
                                        item-text="displayName"
                                        outlined
                                        dense
                                        single-line
                                        hide-details="auto"
                                        >
                                    </v-autocomplete>
                            </v-card-text>
                            <v-card-actions>
                                <v-btn depressed block color="primary" @click="generateVoucher" :loading="generateVoucherLoading">
                                    <v-icon left>mdi-cog</v-icon>
                                    Generate
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
            <v-divider vertical></v-divider>
            <v-col cols="12" md="9">
                <v-row>
                    <v-col>
                        <v-sheet>
                            <div><v-icon left color="success">mdi-arrow-up</v-icon>Total Gain</div>
                            <div class="text-h4">{{formatNumber(totalGain)}}</div>
                        </v-sheet>
                    </v-col>
                    <v-col>
                        <v-sheet>
                            <div><v-icon left color="error">mdi-arrow-down</v-icon>Total Loss</div>
                            <div class="text-h4">{{formatNumber(totalLoss)}}</div>
                        </v-sheet>
                    </v-col>
                    <v-col>
                        <v-sheet>
                            <div>
                                <span v-if="netGainLoss > 0"><v-icon left>mdi-arrow-up</v-icon>Net Gain</span>
                                <span v-else-if="netGainLoss < 0"><v-icon left>mdi-arrow-down</v-icon>Net Loss</span>
                                <span v-else><v-icon left>mdi-swap-vertical</v-icon>Net Gain/Loss</span>
                            </div>
                            <div class="text-h4 font-weight-bold" :class="{'success--text': netGainLoss > 0, 'error--text': netGainLoss < 0}">{{formatNumber(Math.abs(netGainLoss))}}</div>
                        </v-sheet>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-sheet>
                            <v-progress-linear indeterminate v-if="loading"/>
                            <v-simple-table dense>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Account</th>
                                        <th>Net Movement</th>
                                        <th>Averate Rate</th>
                                        <th>System Rate</th>
                                        <th>Gain/Loss</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(d, i) in data" :key="i">
                                        <td>{{ i + 1 }}</td>
                                        <td>{{ d.account_number }} - {{ d.account_name }}</td>
                                        <td :class="{'error--text': d.amount < 0}">{{ formatMoney(d.amount, d.currency_abbreviation) }}</td>
                                        <td>{{ formatNumber(d.actual_rate, 2) }}</td>
                                        <td>{{ formatNumber(d.system_rate, 2) }}</td>
                                        <td :class="{'error--text': d.diff < 0}">
                                            <span v-if="d.system_rate == null" class="error--text"><v-icon left small color="error">mdi-alert-circle</v-icon>Missing Rate</span>
                                            <span v-else>{{ formatMoney(d.diff, baseCurrency.abbreviation) }}</span>
                                        </td>
                                    </tr>
                                    <tr class="grey lighten-3 font-weight-bold">
                                        <td colspan="4"></td>
                                        <td>Total</td>
                                        <td :class="{'error--text': total < 0}">{{ formatMoney(total, baseCurrency.abbreviation) }}</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-sheet>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import exchangeDifference from "./../../services/ledger/exchangeDifference";
import { formatNumber, formatMoney } from "accounting";
import { getTotal } from "./../../utils/accounting";
import moment from "moment";
import { isRequired } from "./../../utils/validations";
import exchangeRateService from "./../../services/ledger/exchangeRates";
import { mapState } from "vuex";
import authorize from '../../utils/authorize';
import currencyService from "./../../services/ledger/currency";
import { createJournalVoucher } from "../../services/journal/journalVoucher"
import { searchAccountByNameOrCode } from "../../services/ledger/account"
import { getDpk } from "../../services/journal";

export default {
    data() {
        return {
            data: [],
            currencies: [],
            loading: false,
            adjustRateLoading: false,
            currenciesLoading: false,
            filter: "",
            year: "",
            account_id: null,
            accountQuery: "",
            accountList: [],
            generateVoucherLoading: false,
        }
    },
    computed: {
        ...mapState("accounting", ["baseCurrency"]),
        // currencies() {
        //     return this.data.reduce((sum, each) => {
        //         let found = sum.find(curr => each.currency_id == curr.id);
        //         if(!found) {
        //             sum.push({id: each.currency_id, abbreviation: each.currency_abbreviation, rate: each.system_rate})
        //         }
        //         return sum;
        //     }, [])
        // },
        total() {
            return getTotal(this.data  , "diff")
        },
        years() {
            let years = [];
            let startYear = 2020;
            let noOfYears = 100;
            for(let i = 0; i < noOfYears; i++) {
                years.push(startYear + i)
            }
            return years;
        },
        totalGain() {
            let data = this.data;
            let gain = 0;

            // return 0 if gain is not an array
            if(!Array.isArray(data)) {
                return gain;
            }

            // calculate total gain
            for(const d of data) {
                let diff = Number(d.diff);
                if(typeof diff != "number") {
                    continue
                }
                if(diff < 0) {
                    gain += diff
                }
            }
            return Math.abs(gain);
        },
        totalLoss() {
            let data = this.data;
            let gain = 0;

            // return 0 if gain is not an array
            if(!Array.isArray(data)) {
                return gain;
            }

            // calculate total gain
            for(const d of data) {
                let diff = Number(d.diff);
                if(typeof diff != "number") {
                    continue
                }
                if(diff > 0) {
                    gain += diff
                }
            }
            return Math.abs(gain);
        },
        netGainLoss() {
            return this.totalGain - this.totalLoss;
        },
        accountName() {
            return this.accountList.find((account) => {
                return account.id == this.account_id
            }).displayName
        }
    },
    watch: {
        
    },
    methods: {
        isRequired,
        formatNumber,
        formatMoney(amount, currency) {
            return formatMoney(amount, { symbol: currency, format: {
                pos: "%s %v",
                neg: "%s (%v)",
                zero: "%s %v"
            } })
        },
        getTotal,        
        searchAccount() {
            searchAccountByNameOrCode({search: this.accountQuery}).then((res)=>{
                this.accountList = res.data
            })
        },
        fetch() {
            this.data = [];
            this.loading = true;
            exchangeDifference.calculateDifference(this.year).then(res => {
                this.data = res.data;
                this.populateSystemRate();
            }).finally(() => {
                this.loading = false;
            })
        },
        adjustRate() {
            let data = {
                root_dimension_id: 1,
                base_currency_id: 1,
                date: moment(`${this.year}-12-31`).format("YYYY-MM-DD"),
                exchange_rate_lines: this.currencies.map(each => {
                    return {
                        currency_id: each.id,
                        rate: each.rate
                    }
                })
            }
            this.adjustRateLoading = true;
            exchangeRateService.storeExchangeRate(data).then(() => {
                this.fetch();
            }).finally(() => {
                this.adjustRateLoading = false;
            })
        },
        hasPermission(code) {
            return authorize(code)
        },
        fetchCurrencies() {
            this.currenciesLoading = true;
            currencyService.index().then(res => {
                this.currencies = res.data.filter(each => each.id != this.baseCurrency.id);
            }).finally(() => {
                this.currenciesLoading = false;
            })
        },
        populateSystemRate() {
            let currencies = this.currencies.map(currency => {
                let found = this.data.find(each => each.currency_id == currency.id);
                let rate = found ? found.system_rate : 0;
                return {
                    ...currency,
                    rate
                }
            })
            this.currencies = currencies;
        },
        generateVoucher() {
            this.generateVoucherLoading = true;
            let voucherData = {
                document_date: this.year + "-12-31",
                reference: "",
                narration: "Profit/Loss Voucher for " + this.year,
                currency_id: 1
            }
            let lines = []
            let total = 0
            for (var d of this.data) {
                if (d.diff != 0) {
                    lines.push({
                        account_id: d.account_id,
                        account: d.account_number + " " + d.account_name + " - " + d.currency_abbreviation,
                        narration: "Profit/Loss for " + this.year,
                        debit: 0,
                        credit: 0,
                        base_debit: d.diff < 0 ? Math.abs(d.diff) : 0,
                        base_credit: d.diff > 0 ? Number(d.diff) : 0,
                    })
                    total = total + Number(d.diff)
                }               
            }
             lines.push({
                        account_id: this.account_id,
                        account: this.accountName,
                        narration: "Profit/Loss for " + this.year,
                        debit: total > 0 ? Math.abs(total) : 0,
                        credit: total < 0 ? Math.abs(total) : 0,
                        base_debit: total > 0 ? total : 0,
                        base_credit: total <  0 ? Math.abs(total): 0,
                    })
            voucherData.lines = lines
            getDpk().then((res) => {
                var dpk = res.data.key
                createJournalVoucher(voucherData, dpk).then((res) => {
                    this.$router.push({name: "JournalEntryDisplay", params: {id: res.data.record.id}})
                }).finally(() => {
                    this.generateVoucherLoading = false;
                })
            }) 
            
        }
    },
    created() {
        this.date = moment().format("YYYY-MM");
        this.fetchCurrencies();
    }
}
</script>

<style>

</style>